import { jsxs as P, jsx as u, Fragment as M } from "react/jsx-runtime";
import { productEventEmitter as h } from "@made-people/grease-product";
import { createContext as N, useContext as j, useEffect as d } from "react";
import { flattener as F } from "@made-people/flatten-object";
import { z as e } from "zod";
import D from "sha.js";
import { checkoutEventEmitter as v } from "@made-people/grease-checkout";
import { orderEventEmitter as G } from "@made-people/grease-order";
import { cartEventEmitter as g } from "@made-people/grease-cart";
import { uiEventEmitter as _ } from "@made-people/grease-ui-events";
import { accountEventEmitter as I } from "@made-people/grease-account";
import { useEffectOnce as V } from "@made-people/react-use-effect-once";
const p = e.object({
  item_id: e.string(),
  item_name: e.string(),
  item_sku: e.string().optional(),
  item_group_id: e.string().optional(),
  affiliation: e.string().optional(),
  coupon: e.string().optional(),
  discount: e.number().optional(),
  index: e.number().optional(),
  item_brand: e.string().optional(),
  // "collection" level, ie "Fila x Mini Rodini"
  item_category: e.string().optional(),
  item_category2: e.string().optional(),
  item_category3: e.string().optional(),
  item_category4: e.string().optional(),
  item_category5: e.string().optional(),
  item_list_id: e.string().optional(),
  // "related_products"
  item_list_name: e.string().optional(),
  // "Related Products"
  item_variant: e.string().optional(),
  location_id: e.string().optional(),
  // a valid Google Place ID
  price: e.number().optional(),
  quantity: e.number().optional(),
  product_image_url: e.string().optional()
}), A = e.object({
  event: e.literal("add_payment_info"),
  ecommerce: e.object({
    currency: e.string(),
    /**
     * The monetary value of the event.
     *
     * - Set value to the sum of (price * quantity) for all items in items. Don't include shipping or tax.
     * - value is typically required for meaningful reporting. If you mark the event as a key event then it's recommended you set value.
     * - currency is required if you set value.
     */
    value: e.number(),
    coupon: e.string().optional(),
    payment_type: e.string().optional(),
    items: e.array(p)
  })
}).passthrough(), B = e.object({
  event: e.literal("add_shipping_info"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    coupon: e.string().optional(),
    // eg "SUMMER_SALE"
    shipping_tier: e.string().optional(),
    // eg "UPS express delivery"
    items: e.array(p).min(1)
  })
}).passthrough(), $ = e.object({
  event: e.literal("add_to_cart"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    items: e.array(p).min(1)
  })
}).passthrough(), O = e.object({
  event: e.literal("begin_checkout"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    coupon: e.string().optional(),
    items: e.array(p).min(1)
  })
}).passthrough(), U = e.object({
  event: e.literal("login"),
  method: e.string()
}), Q = e.object({
  event: e.literal("page_view"),
  ecommerce: e.object({
    currency: e.string()
  }),
  location: e.string()
}).passthrough(), R = e.object({
  email: e.string(),
  phone: e.string(),
  firstname: e.string(),
  lastname: e.string(),
  street: e.string(),
  city: e.string(),
  hashed_email: e.string(),
  hashed_phone: e.string(),
  region: e.string(),
  country: e.string(),
  postal_code: e.string()
}), H = e.object({
  event: e.literal("purchase"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    tax: e.number().optional(),
    shipping: e.number().optional(),
    shipping_tier: e.string().optional(),
    payment_type: e.string().optional(),
    transaction_id: e.string(),
    coupon: e.string().optional(),
    coupon_name: e.string().optional(),
    items: e.array(p).min(1)
  }),
  customer: R
}).passthrough(), W = e.object({
  event: e.literal("remove_from_cart"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    items: e.array(p).min(1)
  })
}).passthrough(), z = e.object({
  event: e.literal("select_item"),
  ecommerce: e.object({
    currency: e.string(),
    item_list_id: e.string().optional(),
    // ignored if set on item level
    item_list_name: e.string().optional(),
    // ignored if set on item level
    items: e.array(p).min(1)
  })
}).passthrough(), K = e.object({
  event: e.literal("sign_up"),
  method: e.string()
}), J = e.object({
  event: e.literal("view_item"),
  ecommerce: e.object({
    currency: e.string(),
    value: e.number(),
    items: e.array(p).min(1)
  })
}).passthrough(), Y = e.object({
  event: e.literal("view_item_list"),
  ecommerce: e.object({
    currency: e.string(),
    item_list_id: e.string().optional(),
    item_list_name: e.string().optional(),
    items: e.array(p).min(1)
  })
}).passthrough(), X = e.discriminatedUnion("event", [
  A,
  B,
  $,
  O,
  U,
  Q,
  H,
  W,
  z,
  K,
  J,
  Y
]), E = [];
function Qt(t) {
  E.includes(t) || E.push(t);
}
function Rt() {
  E.length = 0;
}
function Z(t) {
  return E.length === 0 ? !0 : E.every((o) => {
    try {
      if (o(t) === !1)
        return !1;
    } catch (r) {
      console.error("Error running dataLayer before hook", r);
    }
    return !0;
  });
}
function tt(t) {
  if (typeof window > "u") {
    console.warn(`Tried to push GTM event ${JSON.stringify(t)} on server.`);
    return;
  }
  window.dataLayer = window.dataLayer || [], window.dataLayer.push(t);
}
function w(t) {
  if (typeof window > "u") {
    console.warn(`Tried to push GTM event ${JSON.stringify(t)} on server.`);
    return;
  }
  Z(t) && tt(t);
}
function et(t) {
  const o = X.safeParse(t);
  if (!o.success) {
    console.error(
      "Invalid or unsupported GTM event data - not pushing to datalayer",
      {
        data: t,
        error: o.error
      }
    );
    return;
  }
  w({ ecommerce: null }), w(o.data);
}
function C(t, o) {
  const r = F("///");
  return (i) => {
    const n = r.flatten(t), c = r.flatten(i), s = {
      ...n,
      ...c
    }, a = r.unflatten(s);
    if (o === "customEvent")
      return w(a);
    if (o === "ecommerceEvent")
      return et(a);
    throw new Error(`Unknown function type: ${o}`);
  };
}
const nt = (t) => {
  const o = t.phoneNumber ?? "", r = t.email;
  return {
    email: r,
    firstname: t.firstName,
    lastname: t.lastName,
    phone: o,
    hashed_email: D("sha256").update(r).digest("hex"),
    hashed_phone: D("sha256").update(o).digest("hex"),
    street: (t == null ? void 0 : t.street) ?? "",
    city: (t == null ? void 0 : t.city) ?? "",
    region: (t == null ? void 0 : t.state) ?? "",
    country: (t == null ? void 0 : t.country) ?? "",
    postal_code: (t == null ? void 0 : t.zipCode) ?? ""
  };
};
function ot(t, o = 0, r) {
  let i;
  return t.type === "product" && (i = t.itemDimensions.map((n) => n.value).join(" / ")), {
    ...r.greaseProductBaseToLineItemBase(t.product.base),
    item_sku: t.sku,
    item_variant: i,
    discount: t.price.discounted ? Math.round(
      t.price.priceEachWithoutDiscount.number - t.price.priceEach.number
    ) : 0,
    index: o,
    price: t.price.priceEach.number,
    quantity: t.quantity
  };
}
function rt(t, o, r = 0, i) {
  const n = o.priceInfo.price.number, c = o.priceInfo.discountInfo ? n - o.priceInfo.priceWithoutDiscount.number : 0;
  return {
    ...i.greaseProductBaseToLineItemBase(t),
    item_sku: o.sku,
    discount: Math.round(c),
    index: r,
    price: n,
    quantity: 1
  };
}
const it = (t) => {
  var o, r, i, n, c, s;
  return {
    item_id: t.productId,
    item_name: t.content.name,
    item_category: ((o = t.categories[0]) == null ? void 0 : o.name) || "",
    item_category2: ((r = t.categories[1]) == null ? void 0 : r.name) || "",
    item_category3: ((i = t.categories[2]) == null ? void 0 : i.name) || "",
    item_category4: ((n = t.categories[3]) == null ? void 0 : n.name) || "",
    item_category5: ((c = t.categories[4]) == null ? void 0 : c.name) || "",
    product_image_url: ((s = t.content.media.find((a) => a.type === "image")) == null ? void 0 : s.src) || "",
    item_group_id: t.productKey
  };
}, b = {
  greaseAddressToGtmCustomer: nt,
  greaseLineItemToGtmItem: ot,
  greaseProductToGtmItem: rt,
  greaseProductBaseToLineItemBase: it
}, ct = (t) => {
  const o = {
    ...b,
    ...t
  };
  return (r, i = 0) => o.greaseLineItemToGtmItem(r, i, o);
}, at = (t) => {
  const o = {
    ...b,
    ...t
  };
  return (r, i, n = 0) => o.greaseProductToGtmItem(r, i, n, o);
}, st = (t) => {
  const o = {
    ...b,
    ...t
  };
  return (r) => o.greaseAddressToGtmCustomer(r);
}, S = N(void 0), mt = (t) => {
  const o = {
    mapLineItem: ct(t.mappers),
    mapProduct: at(t.mappers),
    mapCustomer: st(t.mappers)
  }, r = {
    containerIds: t.containerIds,
    mappers: o,
    pushCustomEvent: C(
      t.globalFields,
      "customEvent"
    ),
    pushEcommerceEvent: C(
      t.globalFields,
      "ecommerceEvent"
    )
  };
  return /* @__PURE__ */ P(S.Provider, { value: r, children: [
    /* @__PURE__ */ u(Gt, { ...t.eventListeners }),
    t.children
  ] });
};
mt.displayName = "GtmProvider";
const l = () => {
  const t = j(S);
  if (t === void 0)
    throw new Error("useGtm must be used within a GtmProvider");
  return t;
}, k = () => {
  const t = j(S);
  return (t == null ? void 0 : t.containerIds) ?? [];
}, ut = () => {
  const {
    mappers: { mapProduct: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (i) => {
      i.context.type === "pdp" && o({
        event: "view_item",
        ecommerce: {
          // This is including VAT, which probably is not what we want in case
          // there are comparisons to cart/order operations that are excluding VAT.
          value: i.productData.purchaseInfo.priceInfo.price.number,
          items: [
            t(
              i.productData.base,
              i.productData.purchaseInfo
            )
          ]
        }
      });
    };
    return h.on("productViewed", r), () => {
      h.off("productViewed", r);
    };
  }, []), null;
};
function x(t, o) {
  if (t.type === "plp") {
    if (t.listId)
      return t.listId;
    if (o)
      return o.replace(/(^\/[^/]+\/?)|(\/$)/g, "") || void 0;
    if (typeof window < "u")
      return window.location.pathname.replace(
        /(^\/[^/]+\/?)|(\/$)/g,
        ""
      ) || void 0;
  }
}
function q(t, o) {
  if (t.type !== "plp")
    return;
  if (t.listName)
    return t.listName;
  let r;
  if (o ? r = o.replace(/(^\/[^/]+\/?)|(\/$)/g, "") : typeof window < "u" && (r = window.location.pathname.replace(
    /(^\/[^/]+\/?)|(\/$)/g,
    ""
  )), r)
    return r.split("/").filter(Boolean).map((i) => i.charAt(0).toUpperCase() + i.slice(1)).join(" ");
}
const dt = 20, lt = 500, pt = () => {
  const {
    mappers: { mapProduct: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (i) => {
      if (i.context.type === "plp") {
        window.queuedProductImpressions = window.queuedProductImpressions || [], window.queuedProductImpressions.push(i);
        const c = () => {
          o({
            event: "view_item_list",
            ecommerce: {
              items: window.queuedProductImpressions.map((s) => {
                const a = s.context;
                if (a.type !== "plp")
                  throw new Error(
                    `Expected context type to be 'plp', but got '${a.type}'`
                  );
                return {
                  ...t(
                    s.productData.base,
                    s.productData.purchaseInfo,
                    a.position
                  ),
                  // Lists on item level because that's what works best in reports
                  // Use context values if available, otherwise fall back to URI pathname
                  item_list_id: x(a) ?? a.listId,
                  item_list_name: q(a) ?? a.listName
                };
              })
            },
            additionalData: i.additionalData
          }), window.queuedProductImpressions = [];
        };
        clearTimeout(window.impressionPushTimer), window.queuedProductImpressions.length >= dt ? c() : window.impressionPushTimer = setTimeout(
          c,
          lt
        );
      }
    };
    return h.on("productViewed", r), () => {
      h.off("productViewed", r);
    };
  }, []), null;
}, ht = (t) => t.type === "code";
function T(t) {
  var r;
  return (((r = t.vouchers) == null ? void 0 : r.filter(ht).map((i) => i.code)) ?? []).join(",");
}
function L(t) {
  var r, i;
  const o = (i = (r = t.totals) == null ? void 0 : r.subtotalWithoutTax) == null ? void 0 : i.number;
  return typeof o > "u" && console.warn(
    "Cart value is undefined. We will push 0 value which will break measurement in GA."
  ), o ?? 0;
}
const gt = () => {
  const {
    mappers: { mapLineItem: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (i) => {
      i.cart.items.length !== 0 && o({
        event: "begin_checkout",
        ecommerce: {
          value: L(i.cart),
          coupon: T(i.cart),
          items: i.cart.items.map(t)
        },
        additionalData: i.additionalData
      });
    };
    return v.on("checkoutStarted", r), () => {
      v.off("checkoutStarted", r);
    };
  }, []), null;
}, ft = () => {
  const {
    mappers: { mapLineItem: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (i) => {
      const n = i.cart.paymentMethod;
      if (!n) {
        console.warn(
          "GTM Event Add Payment Info: No payment method set on cart"
        );
        return;
      }
      o({
        event: "add_payment_info",
        ecommerce: {
          value: L(i.cart),
          coupon: T(i.cart),
          payment_type: n.code,
          items: i.cart.items.map(t)
        },
        additionalData: i.additionalData
      });
    };
    return v.on("paymentMethodSelected", r), () => {
      v.off("paymentMethodSelected", r);
    };
  }, []), null;
}, vt = () => {
  const {
    mappers: { mapLineItem: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (i) => {
      const n = i.cart.shippingMethod;
      if (!n) {
        console.warn(
          "GTM Event Add Shipping Info: No shipping method set on cart"
        );
        return;
      }
      o({
        event: "add_shipping_info",
        ecommerce: {
          value: L(i.cart),
          coupon: T(i.cart),
          shipping_tier: n.code,
          items: i.cart.items.map(t)
        },
        additionalData: i.additionalData
      });
    };
    return v.on("shippingMethodSelected", r), () => {
      v.off(
        "shippingMethodSelected",
        r
      );
    };
  }, []), null;
}, yt = [
  "checkout_started_session",
  "add_shipping_info_session",
  "add_payment_info_session"
], Et = () => {
  yt.forEach((t) => {
    sessionStorage.removeItem(t);
  });
}, _t = () => {
  const {
    mappers: { mapLineItem: t, mapCustomer: o },
    pushEcommerceEvent: r
  } = l();
  return d(() => {
    const i = (n) => {
      var c, s, a;
      r({
        event: "purchase",
        ecommerce: {
          value: n.order.totals.subtotalWithoutTax.number,
          tax: (c = n.order.totals.tax) == null ? void 0 : c.number,
          shipping: (s = n.order.totals.shipping) == null ? void 0 : s.number,
          shipping_tier: n.order.shippingMethod,
          payment_type: n.order.paymentMethod,
          transaction_id: n.order.orderNumber,
          coupon: n.order.vouchers.filter(
            (m) => m.type === "code"
          ).map((m) => m.code).join(", "),
          coupon_name: n.order.vouchers.filter(
            (m) => m.type === "code"
          ).map((m) => m.name).join(", "),
          items: n.order.items.map(t)
        },
        customer: o(n.order.shippingAddress),
        additionalData: {
          ...n.additionalData || {},
          gross_sale_amount: n.order.totals.grandTotal.number,
          order_discount: Math.abs(((a = n.order.totals.discount) == null ? void 0 : a.number) ?? 0)
        }
      }), Et();
    };
    return G.on("orderPlaced", i), () => G.off("orderPlaced", i);
  }, []), null;
}, It = () => {
  const {
    mappers: { mapLineItem: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (n) => {
      const c = n.cart.items.find(
        (a) => a.id === n.payload.itemId
      );
      if (!c) {
        console.warn(
          `GTM Event Add To Cart: No item with ID '${n.payload.itemId}' found in cart, not pushing event`,
          { items: n.cart.items }
        );
        return;
      }
      const s = n.additionalData && typeof n.additionalData == "object" && "location" in n.additionalData ? n.additionalData.location : "other";
      o({
        event: "add_to_cart",
        ecommerce: {
          value: c.price.priceEachWithoutTax.number * (n.payload.quantity ?? 1),
          items: [
            {
              ...t(c),
              quantity: n.payload.quantity
            }
          ]
        },
        location: s,
        additionalData: n.additionalData
      });
    }, i = (n) => {
      var m, y;
      const c = (y = (m = n.oldCart) == null ? void 0 : m.items.find(
        (f) => f.lineId === n.payload.lineId
      )) == null ? void 0 : y.quantity;
      if (c === void 0)
        return;
      const s = n.cart.items.find(
        (f) => f.lineId === n.payload.lineId
      );
      if (!s) {
        console.warn(
          `GTM Event Update Quantity: No item with ID '${n.payload.lineId}' found in cart, not pushing event`,
          { items: n.cart.items }
        );
        return;
      }
      const a = n.payload.quantity - c;
      a > 0 && r({
        cart: n.cart,
        payload: {
          itemId: s.id,
          quantity: a
        },
        additionalData: n.additionalData
        // Type cast is fine because we don't use product in the event above
      });
    };
    return g.on("addToCart", r), g.on("updateQuantity", i), () => {
      g.off("addToCart", r), g.off("updateQuantity", i);
    };
  }, []), null;
}, wt = () => {
  const {
    mappers: { mapLineItem: t },
    pushEcommerceEvent: o
  } = l();
  return d(() => {
    const r = (n) => {
      const c = n.payload.removedItem;
      if (!c) {
        console.warn(
          "GTM Event Remove From Cart: No removed item passed, not pushing event",
          { items: n.cart.items }
        );
        return;
      }
      o({
        event: "remove_from_cart",
        ecommerce: {
          value: c.price.priceEachWithoutTax.number * n.payload.quantity,
          items: [
            {
              ...t(c),
              quantity: n.payload.quantity
            }
          ]
        },
        additionalData: n.additionalData
      });
    }, i = (n) => {
      var m, y;
      const c = (y = (m = n.oldCart) == null ? void 0 : m.items.find(
        (f) => f.lineId === n.payload.lineId
      )) == null ? void 0 : y.quantity;
      if (c === void 0)
        return;
      const s = n.cart.items.find(
        (f) => f.lineId === n.payload.lineId
      );
      if (!s) {
        console.warn(
          `GTM Event Update Quantity: No item with ID '${n.payload.lineId}' found in cart, not pushing event`,
          { items: n.cart.items }
        );
        return;
      }
      const a = n.payload.quantity - c;
      a < 0 && r({
        cart: n.cart,
        payload: {
          lineId: n.payload.lineId,
          quantity: Math.abs(a),
          removedItem: s
        }
      });
    };
    return g.on("removeFromCart", r), g.on("updateQuantity", i), () => {
      g.off("removeFromCart", r), g.off("updateQuantity", i);
    };
  }, []), null;
}, bt = () => {
  const { pushEcommerceEvent: t } = l();
  return d(() => {
    const o = (r) => {
      t({
        event: "page_view",
        location: r.to,
        ecommerce: {}
      });
    };
    return _.on("routeChanged", o), () => _.off("routeChanged", o);
  }, []), null;
}, St = (t) => {
  const {
    mappers: { mapProduct: o },
    pushEcommerceEvent: r
  } = l();
  return d(() => {
    const i = (n) => {
      const c = o(
        n.productData.base,
        n.productData.purchaseInfo
      );
      n.context.type === "plp" && (c.item_list_id = x(n.context) ?? n.context.listId, c.item_list_name = q(n.context) ?? n.context.listName), "variant" in n.productData && (c.item_variant = n.productData.variant.variantId), r({
        event: "select_item",
        ecommerce: {
          items: [c]
        },
        additionalData: n.additionalData
      });
    };
    return t.pushSelectItemOnVariantSelected && h.on("productVariantSelected", i), h.on("productClicked", i), () => {
      h.off("productClicked", i), t.pushSelectItemOnVariantSelected && h.off("productVariantSelected", i);
    };
  }, []), null;
}, Tt = () => {
  const { pushCustomEvent: t } = l();
  return d(() => {
    const o = (r) => {
      t({
        event: `${r.feature}_${r.action}`
      });
    };
    return _.on("featureInteraction", o), () => _.off("featureInteraction", o);
  }, [t]), null;
}, Lt = () => {
  const { pushEcommerceEvent: t } = l();
  return d(() => {
    const o = (r) => {
      t({
        event: "sign_up",
        method: r.method
      });
    };
    return I.on("registerSuccess", o), () => {
      I.off("registerSuccess", o);
    };
  }, []), null;
}, Dt = () => {
  const { pushEcommerceEvent: t } = l();
  return d(() => {
    const o = (r) => {
      t({
        event: "login",
        method: r.method
      });
    };
    return I.on("loginSuccess", o), () => {
      I.off("loginSuccess", o);
    };
  }, []), null;
}, Gt = (t) => /* @__PURE__ */ P(M, { children: [
  /* @__PURE__ */ u(It, {}),
  /* @__PURE__ */ u(ft, {}),
  /* @__PURE__ */ u(vt, {}),
  /* @__PURE__ */ u(gt, {}),
  /* @__PURE__ */ u(Dt, {}),
  /* @__PURE__ */ u(bt, {}),
  /* @__PURE__ */ u(_t, {}),
  /* @__PURE__ */ u(wt, {}),
  /* @__PURE__ */ u(St, { ...t.selectItem }),
  /* @__PURE__ */ u(Lt, {}),
  /* @__PURE__ */ u(ut, {}),
  /* @__PURE__ */ u(pt, {}),
  /* @__PURE__ */ u(Tt, {})
] }), Ct = `
  // Initialize the dataLayer like the copy-paste snippet
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event:'gtm.js' });
`, Pt = `
  // Used to prevent google ads rogue referrals
  window.dataLayer.push({
    originalLocation: document.location.protocol + '//' +
    document.location.hostname +
    document.location.pathname +
    document.location.search
  });
`, Mt = `
  let firstBrowserInteractionFired = false
  document.addEventListener("scroll", GTM_firstBrowserInteractionEvent);
  document.addEventListener("mousemove", GTM_firstBrowserInteractionEvent);
  document.addEventListener("touchstart", GTM_firstBrowserInteractionEvent);
  function GTM_firstBrowserInteractionEvent(event) {
    event.currentTarget.removeEventListener(event.type, GTM_firstBrowserInteractionEvent);
    if (firstBrowserInteractionFired) {
      return
    }
    
    firstBrowserInteractionFired = true
    window.dataLayer.push({
      // Event used to trick pagespeed insights to put third party
      // scripts like recaptcha and livechat off the main thread
      //
      // This event should *NOT* be used for GA/FB or any other tracking
      // as some events will not get tracked causing incorrect statistics
      event: 'firstBrowserInteraction'
    });
  }
`, Ht = (t) => {
  const o = k(), r = t.containerIds || o;
  if (!r.length)
    return null;
  const i = t.addOriginalLocation ?? !0, n = t.addFirstBrowserInteractionEvent ?? !0, c = [Ct];
  return i && c.push(Pt), n && c.push(Mt), V(() => {
    if (typeof document < "u" || r.length) {
      const s = document.createElement("script");
      s.innerHTML = c.join(`
`), document.head.appendChild(s), r.forEach((a) => {
        const m = document.createElement("script");
        m.async = !0, m.src = `https://www.googletagmanager.com/gtm.js?id=${a}`, document.head.appendChild(m);
      });
    }
  }), null;
}, Wt = (t) => {
  const o = k(), r = t.containerIds || o;
  return r.length ? /* @__PURE__ */ u(M, { children: r.map((i, n) => /* @__PURE__ */ u("noscript", { children: /* @__PURE__ */ u(
    "iframe",
    {
      title: "gtm",
      src: `https://www.googletagmanager.com/ns.html?id=${i}`,
      height: "0",
      width: "0"
    }
  ) }, n)) }) : null;
};
function zt(t) {
  switch (t.dataModel) {
    case "simple":
      return t.priceInfo.price.number;
    case "bundle":
      return t.bundlePurchaseInfo.priceMin.number;
    case "with_variants":
      const o = t.variants[0];
      return o ? o.priceInfo.price.number : (console.warn("Product has no variants. Returning 0 as the price."), 0);
  }
}
export {
  ft as GtmEventAddPaymentInfo,
  vt as GtmEventAddShippingInfo,
  It as GtmEventAddToCart,
  gt as GtmEventBeginCheckout,
  Tt as GtmEventFeatureInteraction,
  Gt as GtmEventListeners,
  Dt as GtmEventLogin,
  bt as GtmEventPageView,
  _t as GtmEventPurchase,
  wt as GtmEventRemoveFromCart,
  St as GtmEventSelectItem,
  Lt as GtmEventSignUp,
  ut as GtmEventViewItem,
  pt as GtmEventViewItemList,
  mt as GtmProvider,
  Ht as GtmScriptHeadTag,
  Wt as GtmScriptNoscriptTag,
  b as defaultGtmMappers,
  T as getCartCoupons,
  L as getCartValue,
  zt as getPriceFromProduct,
  x as getProductListId,
  q as getProductListName,
  nt as greaseAddressToGtmCustomer,
  ot as greaseLineItemToGtmItem,
  it as greaseProductBaseToLineItemBase,
  rt as greaseProductToGtmItem,
  S as gtmContext,
  R as gtmCustomerSchema,
  et as gtmDataLayerPushEcommerceEvent,
  w as gtmDataLayerPushEvent,
  tt as gtmDataLayerPushRaw,
  A as gtmEcommerceAddPaymentInfoEventSchema,
  B as gtmEcommerceAddShippingInfoEventSchema,
  $ as gtmEcommerceAddToCartEventSchema,
  O as gtmEcommerceBeginCheckoutEventSchema,
  X as gtmEcommerceEventSchema,
  p as gtmEcommerceItemSchema,
  U as gtmEcommerceLoginEventSchema,
  H as gtmEcommercePurchaseEventSchema,
  W as gtmEcommerceRemoveFromCartEventSchema,
  z as gtmEcommerceSelectItemEventSchema,
  K as gtmEcommerceSignUpEventSchema,
  J as gtmEcommerceViewItemEventSchema,
  Y as gtmEcommerceViewItemListEventSchema,
  Q as gtmGeneralPageViewSchema,
  C as makeDataLayerPushFunction,
  st as makeGreaseAddressToGtmCustomerMapper,
  ct as makeGreaseLineItemMapper,
  at as makeGreaseProductMapper,
  Qt as registerDataLayerBeforeHook,
  Rt as resetDataLayerBeforeHooks,
  Z as runDataLayerBeforeHooks,
  l as useGtm,
  k as useGtmContainerIds
};
